import type { Chat } from '@ztechapps/common.contract'
import { ChatStatusEnum, MergeRefusalCodeEnum } from '@ztechapps/common.contract'
import isEmpty from 'lodash/isEmpty'
import some from 'lodash/some'
import uniq from 'lodash/uniq'
import { normalizeSenderAddress } from '@ztechapps/common.utils'
import { documentLinkFields, documentLinkKinds } from './documentLinkKinds'

const crossCustomerMessage = 'Cannot merge conversations that belong to different companies, customers or orders.'

export const mergeRefusalMessages: Record<MergeRefusalCodeEnum, string> = {
  [MergeRefusalCodeEnum.MERGE_SELF]: 'A conversation cannot be merged into itself.',
  [MergeRefusalCodeEnum.MERGE_SOURCE_ALREADY_MERGED]: 'This conversation was already merged into another conversation.',
  [MergeRefusalCodeEnum.MERGE_TARGET_ALREADY_MERGED]: 'Cannot merge into a conversation that was already merged.',
  [MergeRefusalCodeEnum.MERGE_TARGET_CANCELLED]: 'Cannot merge into a Cancelled conversation.',
  [MergeRefusalCodeEnum.MERGE_CROSS_COMPANY]: crossCustomerMessage,
  [MergeRefusalCodeEnum.MERGE_CROSS_CUSTOMER]: crossCustomerMessage,
}

// A missing id never blocks a merge: an unassigned orphan is exactly what "merge into an existing
// conversation" folds into an order. Only two sides carrying different ids clash.
const clashes = (a?: string, b?: string): boolean => !isEmpty(a) && !isEmpty(b) && a !== b

/**
 * The one rule set of a merge, as the code of the first rule the pair breaks, or `undefined` when it
 * breaks none. `assertMergeable` refuses on it and the merge dialog's list is built to agree with it.
 * The caller hands over each `requester` hydrated: a stored chat holds only `{ _id }`, and the company
 * identity lives on the account. A tombstone is read from `mergedInto` alone — an agent can move a
 * merged conversation out of Cancelled, so its status proves nothing — and a merged target is judged
 * before a Cancelled one, since every tombstone starts out Cancelled and "already merged" is the more
 * specific reason. Customer: two requesters of one company match whatever their sender addresses, two
 * of different companies never do; only when either side has no company do the sender addresses
 * decide, and a missing address matches. Document links: only two different ids of one kind clash, and
 * two sides carrying links of different kinds are refused even when no single id clashes.
 */
export const getMergeRefusalCode = (source: Chat, target: Chat): MergeRefusalCodeEnum | undefined => {
  if (source?._id === target?._id) return MergeRefusalCodeEnum.MERGE_SELF
  if (!isEmpty(source?.mergedInto)) return MergeRefusalCodeEnum.MERGE_SOURCE_ALREADY_MERGED
  if (!isEmpty(target?.mergedInto)) return MergeRefusalCodeEnum.MERGE_TARGET_ALREADY_MERGED
  if (target?.status === ChatStatusEnum.Cancelled) return MergeRefusalCodeEnum.MERGE_TARGET_CANCELLED

  const sourceCompanyId = source?.requester?.company?._id
  const targetCompanyId = target?.requester?.company?._id
  if (clashes(sourceCompanyId, targetCompanyId)) return MergeRefusalCodeEnum.MERGE_CROSS_COMPANY

  const sourceKinds = documentLinkKinds(source)
  const targetKinds = documentLinkKinds(target)
  const crossKind = !isEmpty(sourceKinds) && !isEmpty(targetKinds) && uniq([...sourceKinds, ...targetKinds]).length > 1
  const crossLink = some(documentLinkFields, (field) => clashes(source?.metadata?.[field], target?.metadata?.[field]))
  const sameCompany = !isEmpty(sourceCompanyId) && sourceCompanyId === targetCompanyId
  const crossAddress =
    !sameCompany &&
    clashes(
      normalizeSenderAddress(source?.sender?.profile?.email),
      normalizeSenderAddress(target?.sender?.profile?.email)
    )
  if (crossAddress || crossLink || crossKind) return MergeRefusalCodeEnum.MERGE_CROSS_CUSTOMER

  return undefined
}
